import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { RawItemForm } from "./components/RawItemForm";
import { RawItemList } from "./components/RawItemList";
import { useRawItems } from "./hooks/useRawItems";
import type { RawItem } from "@/types/models";

export default function InsumosPage() {
  const {
    store,
    rawItems,
    loading,
    createRawItem,
    updateRawItem,
    removeRawItem,
    saving,
    deleting,
  } = useRawItems();

  const [selectedRawItem, setSelectedRawItem] = useState<RawItem | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSubmit = (input: Parameters<typeof createRawItem>[0]) => {
    setMessage(null);
    setErrorMessage(null);

    if (input.id) {
      updateRawItem(input, {
        onCompleted: () => {
          setMessage("Insumo atualizado com sucesso.");
          setSelectedRawItem(null);
        },
        onError: (message) => setErrorMessage(message),
      });
      return;
    }

    createRawItem(input, {
      onCompleted: () => setMessage("Insumo criado com sucesso."),
      onError: (message) => setErrorMessage(message),
    });
  };

  const handleDelete = (rawItem: RawItem) => {
    if (!window.confirm(`Excluir o insumo "${rawItem.name}"?`)) return;

    setMessage(null);
    setErrorMessage(null);
    removeRawItem(rawItem.id, {
      onCompleted: () => {
        setMessage("Insumo excluído.");
        if (selectedRawItem?.id === rawItem.id) setSelectedRawItem(null);
      },
      onError: (message) => setErrorMessage(message),
    });
  };

  if (loading) {
    return (
      <div className="container-elegant page-padding space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="container-elegant page-padding">
        <PageHeader title="Insumos" />
        <section className="max-w-xl border border-primary-200 dark:border-primary-800 panel-padding">
          <h2 className="text-xl font-light text-primary-900 dark:text-white mb-3">
            Crie sua loja primeiro
          </h2>
          <p className="text-sm font-light text-primary-600 dark:text-primary-400 mb-6">
            Você precisa configurar uma loja antes de cadastrar insumos para precificação.
          </p>
          <Button asChild>
            <Link to="/store/settings">CONFIGURAR LOJA</Link>
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="container-elegant page-padding">
      <PageHeader
        title="Insumos"
        description={`Cadastre materiais e custos unitários para montar receitas e calcular preços na loja ${store.name}.`}
      />

      {message && (
        <p className="mb-6 text-sm font-light text-green-700 dark:text-green-400">{message}</p>
      )}

      {errorMessage && (
        <p className="mb-6 text-sm font-light text-red-700 dark:text-red-400">{errorMessage}</p>
      )}

      <div className="grid gap-10 lg:grid-cols-2">
        <RawItemList
          rawItems={rawItems}
          selectedId={selectedRawItem?.id}
          deleting={deleting}
          onEdit={setSelectedRawItem}
          onDelete={handleDelete}
        />
        <RawItemForm
          selectedRawItem={selectedRawItem}
          saving={saving}
          onSubmit={handleSubmit}
          onCancelEdit={() => setSelectedRawItem(null)}
        />
      </div>
    </div>
  );
}
